import React, { useState, KeyboardEvent, useRef, useEffect } from 'react';
import { Textarea } from '../../ui/textarea';
import { Button } from '../../ui/button';
import { Send } from 'lucide-react';
import { ModelSelector, Model } from '../controls/ModelSelector';

interface MessageInputProps {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;

  // Model selection (optional)
  showModelSelector?: boolean;
  models?: Model[];
  selectedModel?: string;
  onModelChange?: (model: string) => void;

  // Events
  onFocus?: () => void;
  onBlur?: () => void;
}

export const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  disabled = false,
  placeholder = 'Type a message...',
  className = '',
  showModelSelector = false,
  models = [],
  selectedModel = '',
  onModelChange,
  onFocus,
  onBlur,
}) => {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      // Start with single line height
      textareaRef.current.style.height = '24px';
      // Let it grow based on content
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 100; // ~5 lines
      textareaRef.current.style.height =
        Math.min(scrollHeight, maxHeight) + 'px';
    }
  }, [message]);

  const handleSend = () => {
    if (message.trim() && !disabled) {
      onSendMessage(message);
      setMessage('');
      // Refocus the textarea after sending
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 0);
    }
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      className={`w-full rounded-md bg-background focus:outline-none focus:ring-0 ${className}`}
      data-message-input-container="true"
    >
      <Textarea
        ref={textareaRef}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyPress}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        rows={1}
        className="resize-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 p-0 mx-3 mt-3 mb-2 overflow-y-auto scrollbar-thin"
        style={{
          width: 'calc(100% - 24px)',
          padding: '0',
        }}
      />

      <div className="flex items-center justify-between px-2 pb-2 pt-1">
        {showModelSelector && models.length > 0 && selectedModel && onModelChange ? (
          <ModelSelector
            selectedModel={selectedModel}
            models={models}
            onModelChange={onModelChange}
            disabled={disabled}
          />
        ) : (
          <div />
        )}

        <Button
          onClick={handleSend}
          disabled={disabled || !message.trim()}
          size="sm"
          className="ml-auto"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
